import { useContext, useEffect, useMemo } from 'react';
import CreateASpoolContext from '../../../../context/createASpool/createASpoolContext';
import { formatPercentages, splitStringAndJoin } from '../../../../helpers/formats';
import {
    EmptyDataRow,
    Table,
    TableRow,
    TableRowContainer,
} from '../../../../components/Table';
import tabsContext from '../../../../components/Tabs/tabsContext';
import Loading from '../../../../components/Loading';
import BarChart from '../../../../components/BarChart';
import useStrategyAllocationDetails from '../../../../hooks/web3/app/useStrategyAllocationDetails';
import { RiskTolerance } from '../../../../types/spool-local';
import InfoPopUp from '../../../../components/InfoPopUp';
import { ErrorIcon } from '../../../../assets/icons';
import appContext from '../../../../context/app/appContext';

type AllocationDetailsType = {
    [x: string]: {
        apy: string;
        risk: string;
        allocation: string;
    };
};

type AllocationType = {
    amount: Record<string, string>;
    projectedApy: number;
};

const EstimatedALlocationDetails = () => {
    const { riskTolerance, setAllocations, setBaseApy, setStepCompletedStatus } =
        useContext(CreateASpoolContext);
    const { updateHeight } = useContext(tabsContext);

    const {
        // strategyAllocationDetails,
        error,
    } = useStrategyAllocationDetails();
    const { chainStrategies } = useContext(appContext);

    const strategyAllocationData = useMemo(() => {
        // if (!strategyAllocationDetails) return;

        const strategyAllocationDetails = {
            allocations: [
                ['0xf36d05c1b7810013334ce6f1986aaefde1eb34fc', '1000'],
                ['0xc7c5652874de32a5ce305bd9195b7c665281648e', '20000'],
                ['0x9e9d4d747a3dfe3d135077cfa1baf98599cf5b80', '10000'],
                ['0x335bd363fe258f0af80a2b664c1c9b45ab6fce9d', '2000'],
                ['0x7957901a8941fad236483eaa0332880c2f507654', '10000'],
                ['0x87581d026eb25269e3ebb61a47025892c6a1e79c', '2000'],
                ['0x0e3f4f0e9c6ac6dbd8f662d5874dec0c2aae727e', '100'],
                ['0xb1f298e91cfcae8f7f8bd98b19f3d0868a47b254', '20000'],
                ['0x0b7f42dc09a48d2d40f0226a79be86c71da44ba6', '1000'],
                ['0x2394d04f0071397d799d9ed392e631ac2499fed7', '200'],
                ['0x099f65906d09fea4628d9f0b5e0637293a79e28b', '10'],
                ['0x87f0c2b4659a79a9bbe1bfc3889eaba55ac0350d', '20000'],
                ['0xbfa7646f4a20cc303501ab7c9ee4416401ccd532', '20000'],
            ],
            apys: {
                '0xf36d05c1b7810013334ce6f1986aaefde1eb34fc': '1',
                '0xc7c5652874de32a5ce305bd9195b7c665281648e': '2',
                '0x9e9d4d747a3dfe3d135077cfa1baf98599cf5b80': '5',
                '0x335bd363fe258f0af80a2b664c1c9b45ab6fce9d': '11',
                '0x7957901a8941fad236483eaa0332880c2f507654': '12',
                '0x87581d026eb25269e3ebb61a47025892c6a1e79c': '1',
                '0x0e3f4f0e9c6ac6dbd8f662d5874dec0c2aae727e': '2',
                '0xb1f298e91cfcae8f7f8bd98b19f3d0868a47b254': '40',
                '0x0b7f42dc09a48d2d40f0226a79be86c71da44ba6': '3',
                '0x2394d04f0071397d799d9ed392e631ac2499fed7': '2',
                '0x099f65906d09fea4628d9f0b5e0637293a79e28b': '15',
                '0x31c2f932052f7861ac3f301c584d6888b6e6896d': '13',
                '0x87f0c2b4659a79a9bbe1bfc3889eaba55ac0350d': '18',
                '0xbfa7646f4a20cc303501ab7c9ee4416401ccd532': '09',
            },
            risks: {
                '0xf36d05c1b7810013334ce6f1986aaefde1eb34fc': '1',
                '0xc7c5652874de32a5ce305bd9195b7c665281648e': '2',
                '0x9e9d4d747a3dfe3d135077cfa1baf98599cf5b80': '3',
                '0x335bd363fe258f0af80a2b664c1c9b45ab6fce9d': '4',
                '0x7957901a8941fad236483eaa0332880c2f507654': '5',
                '0x87581d026eb25269e3ebb61a47025892c6a1e79c': '6',
                '0x0e3f4f0e9c6ac6dbd8f662d5874dec0c2aae727e': '7',
                '0xb1f298e91cfcae8f7f8bd98b19f3d0868a47b254': '8',
                '0x0b7f42dc09a48d2d40f0226a79be86c71da44ba6': '9',
                '0x2394d04f0071397d799d9ed392e631ac2499fed7': '1',
                '0x099f65906d09fea4628d9f0b5e0637293a79e28b': '2',
                '0x31c2f932052f7861ac3f301c584d6888b6e6896d': '3',
                '0x87f0c2b4659a79a9bbe1bfc3889eaba55ac0350d': '4',
                '0xbfa7646f4a20cc303501ab7c9ee4416401ccd532': '5',
            },
        };

        return Object.entries(
            strategyAllocationDetails.allocations[
                riskTolerance as unknown as RiskTolerance
            ]
        ).reduce((allocationDetails, [strategyId, allocation]) => {
            return {
                ...allocationDetails,
                [strategyId]: {
                    apy: strategyAllocationDetails.apys[strategyId],
                    risk: strategyAllocationDetails.risks[strategyId],
                    allocation: allocation.toString(),
                },
            };
        }, {} as AllocationDetailsType);
    }, [riskTolerance]);

    useEffect(() => {
        updateHeight();

        if (strategyAllocationData) {
            const allocations: AllocationType = Object.entries(
                strategyAllocationData
            ).reduce(
                (allocationsObject, [strategyId, { allocation, apy }]) => ({
                    ...allocationsObject,
                    amount: {
                        ...allocationsObject.amount,
                        [strategyId]: allocation,
                    },
                    projectedApy:
                        allocationsObject.projectedApy + (+apy * +allocation) / 100,
                }),
                { amount: {}, projectedApy: 0 }
            );
            setAllocations(allocations.amount);
            setBaseApy(allocations.projectedApy);
            setStepCompletedStatus(4, true);
        }
    }, [
        strategyAllocationData,
        updateHeight,
        setAllocations,
        setBaseApy,
        setStepCompletedStatus,
    ]);

    const highestAllocation = strategyAllocationData
        ? Object.values(strategyAllocationData).reduce(
              (largest, { allocation }) =>
                  +allocation > largest ? +allocation : largest,
              0
          )
        : 0;

    return (
        <div className='estimated-allocation-details'>
            <div className='title'>
                <span>Estimated Allocation Details</span>
                <div className='line' />
            </div>
            <Table
                numOfColumns={5}
                headers={['#', 'Strategy', 'APY', 'Allocation', 'Risk Rating']}
                dataLength={
                    strategyAllocationData
                        ? Object.keys(strategyAllocationData).length
                        : 0
                }
            >
                <TableRowContainer>
                    <Loading
                        isLoading={!strategyAllocationData && !error}
                        text='Loading allocations...'
                    >
                        {strategyAllocationData ? (
                            Object.entries(strategyAllocationData)
                                .sort(([_, a], [__, b]) => +b.allocation - +a.allocation)
                                .map(
                                    (
                                        [strategyAddress, { apy, risk, allocation }],
                                        index
                                    ) => {
                                        return (
                                            <TableRow key={index}>
                                                <div>{index + 1}</div>
                                                <div>
                                                    {chainStrategies[
                                                        strategyAddress.toLowerCase()
                                                    ]?.name || 'Strategy'}
                                                </div>
                                                <div>
                                                    <div className='apy'>
                                                        {formatPercentages(
                                                            +apy * 100 || 0
                                                        )}
                                                        %
                                                        {chainStrategies[
                                                            strategyAddress.toLowerCase()
                                                        ]?.negativeApyText && (
                                                            <InfoPopUp
                                                                trigger={<ErrorIcon />}
                                                                className='warning-pop-up'
                                                            >
                                                                {
                                                                    chainStrategies[
                                                                        strategyAddress.toLowerCase()
                                                                    ]?.negativeApyText
                                                                }
                                                            </InfoPopUp>
                                                        )}
                                                    </div>
                                                    <span className='column-label'>
                                                        Apy
                                                    </span>
                                                </div>
                                                <div>
                                                    <BarChart
                                                        value={
                                                            +formatPercentages(
                                                                allocation || 0
                                                            )
                                                        }
                                                        total={highestAllocation}
                                                        text={`${+formatPercentages(
                                                            splitStringAndJoin(
                                                                allocation,
                                                                -2,
                                                                '.'
                                                            ) || 0
                                                        )}%`}
                                                    />
                                                    {/* {splitStringAndJoin(allocation, -2, '.')}% */}
                                                    <span className='column-label'>
                                                        Allocation
                                                    </span>
                                                </div>
                                                <div>
                                                    {risk}
                                                    <span className='column-label'>
                                                        Risk Rating
                                                    </span>
                                                </div>
                                            </TableRow>
                                        );
                                    }
                                )
                        ) : (
                            <EmptyDataRow text='No allocations found...' />
                        )}
                    </Loading>
                </TableRowContainer>
            </Table>
        </div>
    );
};

export default EstimatedALlocationDetails;
